import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSeo } from "../hooks/useSeo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (s) => (s.startsWith("http") ? s : `${BACKEND}${s}`);

const Watermark = ({ className = "w-20 sm:w-24" }) => (
  <img src="/brand/watermark-white.png" alt="" aria-hidden="true"
    className={`pointer-events-none select-none absolute bottom-2 right-2 opacity-70 ${className}`}
    style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.55))" }} />
);

function BeforeAfter({ pairs }) {
  if (!pairs.length) return null;
  return (
    <section className="mt-12" data-testid="gallery-before-after">
      <div className="label-eyebrow mb-2">Before &amp; after</div>
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight max-w-2xl">Tired and dated, completely transformed.</h2>
      <p className="mt-3 text-[#6B7280] text-base max-w-2xl">
        Real rear elevations we've reimagined — the same house, a brand-new ground floor. Drag your eye from the
        before to the after.
      </p>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {pairs.map((p, idx) => (
          <figure key={p.label || `pair-${idx}`} data-testid={`before-after-${idx}`} className="de-card overflow-hidden">
            <div className="grid grid-cols-2">
              {[["Before", p.before], ["After", p.after]].map(([tag, src]) => (
                <div key={tag} className="relative aspect-[4/3] overflow-hidden bg-[#F4F4F4]">
                  <img src={resolveSrc(src)} alt={`${p.label} — ${tag.toLowerCase()}`} loading="lazy" className="w-full h-full object-cover" />
                  <span className={`absolute top-2 left-2 text-[10px] font-black uppercase tracking-widest px-2 py-1 ${tag === "After" ? "bg-[#C79A3B] text-white" : "bg-white/90 text-[#12263A]"}`}>{tag}</span>
                  <Watermark className="w-14 sm:w-16" />
                </div>
              ))}
            </div>
            <figcaption className="p-4 text-sm text-[#12263A] font-medium">{p.label}</figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [beforeAfter, setBeforeAfter] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  useSeo({
    title: "Extension Gallery — Real Rear Extensions, Before & After | The Extension Works",
    description: "Browse completed single-storey rear extensions — exteriors, kitchens and interiors, plus before-and-after transformations from The Extension Works, a trusted UK building contractor.",
    canonical: "https://theextensionworks.co.uk/gallery",
    image: "https://theextensionworks.co.uk/brand/og-image.png",
  });

  useEffect(() => {
    api.get("/public/gallery").then((r) => {
      setItems(r.data.items || []);
      setBeforeAfter(r.data.before_after || []);
    }).catch(() => {});
  }, []);

  const cats = ["All", ...Array.from(new Set(items.map((i) => i.category)))];
  const shown = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <main data-testid="gallery-page" className="px-6 md:px-12 lg:px-16 pt-16 pb-24 max-w-7xl">
      <div className="label-eyebrow mb-6">Gallery</div>
      <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
        Completed work, photographed.
      </h1>
      <p className="mt-6 text-[#6B7280] leading-relaxed text-base max-w-2xl">
        Real extensions we've designed and built — footings to finish. The 3D configurator on our quote
        tool is a scale model; these are the finished article.
      </p>

      <BeforeAfter pairs={beforeAfter} />

      <div className="mt-16 label-eyebrow mb-6">The full gallery</div>

      <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`gallery-filter-${c.toLowerCase()}`}
            onClick={() => setFilter(c)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${
              filter === c
                ? "bg-[#12263A] text-white border-[#12263A]"
                : "border-[#E5E7EB] bg-white hover:border-[#12263A]"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-10 gap-5 [column-fill:_balance] columns-1 sm:columns-2 lg:columns-3">
        {shown.map((it, idx) => (
          <figure
            key={it.id}
            data-testid={`gallery-item-${idx}`}
            onClick={() => setActive(it)}
            className="mb-5 break-inside-avoid group relative overflow-hidden bg-[#F4F4F4] cursor-pointer"
          >
            <img
              src={resolveSrc(it.src)}
              alt={it.caption}
              loading="lazy"
              className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.04]"
            />
            <Watermark />
            <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/75 to-transparent text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {it.caption}
            </figcaption>
          </figure>
        ))}
      </div>

      {items.length === 0 && (
        <div className="mt-10 text-[#6B7280]" data-testid="gallery-empty">Loading gallery…</div>
      )}

      {active && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setActive(null)}
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-6"
        >
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <img src={resolveSrc(active.src)} alt={active.caption} className="w-full h-auto max-h-[80vh] object-contain" />
              <Watermark className="w-28 sm:w-32" />
            </div>
            <div className="mt-4 flex items-center justify-between text-white">
              <span className="text-sm">{active.caption}</span>
              <button
                data-testid="gallery-lightbox-close"
                onClick={() => setActive(null)}
                className="text-xs font-bold uppercase tracking-widest border border-white/40 px-4 py-2 hover:bg-white hover:text-black transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
